import { tabNames } from '@va/constants';
import { setGlobalFilter } from '@va/dashboard/actions/ui';
import { getWebsite } from '@va/dashboard/selectors/core';
import { getActiveTab, getGlobalFilter } from '@va/dashboard/selectors/ui';
import { FreePlanUpgradeTooltipContent } from '@va/dashboard/ui/components';
import { useFreePlanStatus, useRefreshDateFilter } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { isSettingActiveWebsiteInProgress } from '@va/standalone/shared/selectors';
import { DateRangeKey } from '@va/types/time';
import { DatePickerV2 } from '@va/ui/components/date-picker';
import { PlacementType } from '@va/ui/components/date-picker/types';

import {
  DATEPICKER_DEFAULT_SHORTCUTS,
  DATEPICKER_FREE_PLAN_HISTORY_SHORTCUT,
  fromDayjsToMoment,
  fromMomentToDayjs,
  is3asApp,
  isStandaloneApp,
  isWixApp,
} from '@va/util/helpers';
import dayjs, { Dayjs } from 'dayjs';
import { Moment } from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type DatePickerContainerProps = {
  featureReleaseDate?: number;
  enforceDateLimit?: boolean;
  installDate?: number;
  placement?: PlacementType;
  size?: 'small' | 'medium';
  onClick?: () => void;
  onHover?: () => void;
};

function DatePickerContainer({
  featureReleaseDate,
  enforceDateLimit = false,
  installDate: installDateProp,
  placement,
  size = 'medium',
  onClick,
  onHover,
}: DatePickerContainerProps) {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const filter = useSelector(getGlobalFilter);
  const website = useSelector(getWebsite);
  const installDate = installDateProp ?? website.installDate;
  const installDateInstance = dayjs(installDate * 1000).tz(window.timezone);
  const today = dayjs().tz(window.timezone).endOf('day');
  const { hasActiveFreePlan, freePlanHistoryStartDate } = useFreePlanStatus();
  const isSwitchingWebsite = useSelector(isSettingActiveWebsiteInProgress);
  const activeTab = useSelector(getActiveTab);
  // VISA-12815 - Do not limit date picker for recordings tab when free plan is active
  const limitAccordingToFreePlan = useMemo(
    () => hasActiveFreePlan && activeTab !== tabNames.recordings,
    [activeTab, hasActiveFreePlan],
  );

  const onPeriodChange = useCallback(
    ([from, until]: [Moment, Moment], dateRangeKey?: DateRangeKey) => {
      dispatch(setGlobalFilter({ from, until, dateRangeKey }));
    },
    [dispatch],
  );

  const getClosestDate = useCallback(() => {
    const featureDate = featureReleaseDate ? dayjs(featureReleaseDate * 1000).tz(window.timezone) : null;

    const datesToCompare = [];
    if (featureDate) datesToCompare.push(featureDate);
    if (limitAccordingToFreePlan) {
      datesToCompare.push(freePlanHistoryStartDate);
    } else {
      datesToCompare.push(installDateInstance);
    }
    return datesToCompare.reduce((closest, current) =>
      Math.abs(current.diff(today)) < Math.abs(closest.diff(today)) ? current : closest,
    );
  }, [featureReleaseDate, limitAccordingToFreePlan, freePlanHistoryStartDate, installDateInstance, today]);

  const minimumDate = useMemo(() => getClosestDate().startOf('day'), [getClosestDate]);
  const maximumDate = dayjs().tz(window.timezone).endOf('day');

  const getDatePickerTooltip = () => {
    const closestDate = getClosestDate();

    if (limitAccordingToFreePlan && closestDate.isSame(dayjs(freePlanHistoryStartDate).tz(window.timezone))) {
      return <FreePlanUpgradeTooltipContent />;
    }

    if (featureReleaseDate && closestDate.isSame(dayjs(featureReleaseDate * 1000).tz(window.timezone))) {
      return translate('filter.featureReleaseDate', {
        releaseDate: closestDate.format('L'),
      });
    }

    return translate('filter.installedToday');
  };

  useRefreshDateFilter();

  // VISA-14057 - adjust dates according to minimumDate
  useEffect(() => {
    if (website.inProgress || isSwitchingWebsite) return;

    const dayJsFrom = fromMomentToDayjs(filter.from);
    const dayJsUntil = fromMomentToDayjs(filter.until);

    if (dayJsFrom.isSameOrAfter(minimumDate.startOf('day'))) return;

    if (dayJsUntil.isSameOrAfter(minimumDate.startOf('day'))) {
      dispatch(
        setGlobalFilter({
          from: fromDayjsToMoment(minimumDate.startOf('day')),
          until: filter.until,
          dateRangeKey: filter.dateRangeKey,
        }),
      );
      return;
    }

    dispatch(
      setGlobalFilter({
        from: fromDayjsToMoment(minimumDate.startOf('day')),
        until: fromDayjsToMoment(maximumDate),
        dateRangeKey: filter.dateRangeKey,
      }),
    );
  }, [
    dispatch,
    filter.from,
    filter.until,
    filter.dateRangeKey,
    maximumDate,
    minimumDate,
    website.inProgress,
    isSwitchingWebsite,
  ]);

  const shortcuts = [...DATEPICKER_DEFAULT_SHORTCUTS];
  if (isStandaloneApp() || isWixApp() || is3asApp()) {
    shortcuts.splice(5, 0, DATEPICKER_FREE_PLAN_HISTORY_SHORTCUT);
  }

  const convertedValue: [Dayjs, Dayjs] = [fromMomentToDayjs(filter.from), fromMomentToDayjs(filter.until)];

  return (
    <DatePickerV2
      onChange={([from, until], dateRangeKey) =>
        onPeriodChange([fromDayjsToMoment(from), fromDayjsToMoment(until)], dateRangeKey)
      }
      shouldDisablePastDates={limitAccordingToFreePlan || enforceDateLimit}
      trackedDataStartDate={enforceDateLimit ? minimumDate : installDateInstance}
      tooltipContent={getDatePickerTooltip()}
      value={convertedValue}
      placement={placement}
      shortcuts={shortcuts}
      minDate={minimumDate}
      maxDate={maximumDate}
      onClick={onClick}
      onHover={onHover}
      size={size}
    />
  );
}

export default DatePickerContainer;
